<template>
  <section class="wa-signin" data-test="wa-signin-steps">
    <div v-if="number" class="wa-signin__number" data-test="wa-signin-number">
      <p class="wa-signin__eyebrow">The phone number to enter in Facebook</p>
      <p class="wa-signin__value" data-test="wa-signin-number-value">{{ number.display }}</p>
      <p v-if="number.country" class="wa-signin__country" data-test="wa-signin-number-country">
        Country: {{ number.country }}
      </p>
      <p class="wa-signin__rule" data-test="wa-signin-number-rule">
        This is your practice&rsquo;s Teamio WhatsApp number. Enter exactly this number. If your
        practice also uses the WhatsApp Business app on a different number, do not enter that
        one &mdash; it stays on the app, untouched.
      </p>
    </div>
    <div v-else class="wa-signin__number wa-signin__number--missing" data-test="wa-signin-number-missing">
      <p class="wa-signin__rule">
        When Facebook asks for a phone number, enter your practice&rsquo;s Teamio WhatsApp number
        exactly. If you are not sure which number that is, stop and contact Teamio before you
        start.
      </p>
    </div>

    <p class="wa-signin__heading">In the Facebook window</p>
    <ol class="wa-signin__steps" data-test="wa-signin-step-list">
      <li>
        Sign in with your Facebook account &mdash; the one that manages your practice&rsquo;s
        business on Facebook.
      </li>
      <li data-test="wa-signin-step-name">
        Choose your practice&rsquo;s business, or create one with your practice&rsquo;s name.
        For the WhatsApp account name and the display name (the name patients see), enter
        <strong v-if="name">{{ name }}</strong><template v-else>your practice&rsquo;s name</template>.
      </li>
      <li data-test="wa-signin-step-number">
        When Facebook asks for a phone number, enter
        <strong v-if="number" class="wa-signin__nowrap">{{ number.display }}</strong><template v-else>your Teamio WhatsApp number</template><template v-if="number && number.country">
          and choose <strong class="wa-signin__nowrap">{{ number.country }}</strong> as the country</template>.
        Check every digit before you continue.
      </li>
      <li data-test="wa-signin-step-code">
        When Facebook asks how to send the verification code, choose <strong>Text message</strong>.
        The code goes to the Teamio number, not to your phone. Teamio receives it and reads it to
        you, so do this part while you are on the phone with our team.
      </li>
      <li>When Facebook says you are connected, close the window. We take it from there.</li>
    </ol>

    <div class="wa-signin__stop" data-test="wa-signin-stop">
      <p class="wa-signin__heading wa-signin__heading--stop">Stop and contact Teamio if Facebook:</p>
      <ul>
        <li>offers to connect your WhatsApp Business app</li>
        <li>says the number is already used on WhatsApp</li>
        <li>asks you to delete an account or a phone number &mdash; nothing needs to be deleted</li>
        <li>says your business can&rsquo;t add more phone numbers</li>
      </ul>
    </div>
  </section>
</template>

<script>
/**
 * What to do inside Facebook's WhatsApp sign-in, step by step — naming the EXACT number to enter.
 *
 * ── WHY THIS EXISTS (2026-09-11) ─────────────────────────────────────────────────────────────────
 * Facebook's sign-in always asks a practice with no WhatsApp account yet for a phone number, and
 * texts a verification code to it (proven in the Mint prod pilot, 2026-08-03 — WHATSAPP.md). It
 * accepts any number. A practice that keeps its WhatsApp Business app number (The Oral Care Centre,
 * Barbados) has TWO: the app number and the number Teamio bought. The right one is the Teamio number
 * (the site's GENERAL_PHONE — the number the sender registers on and the code is texted to). The
 * wrong one is the only mistake in this flow that can cost a practice its app: Facebook would say the
 * number is already on WhatsApp, and deleting that account to "fix" it loses the app and its history.
 * The old screen said "Follow Facebook's prompts" and never named a number.
 *
 * ── ONE COMPONENT, TWO SCREENS ───────────────────────────────────────────────────────────────────
 * Rendered by the public link page (WhatsAppConnect.vue — number read from the link) and by the
 * wizard's Connect screen (WizardConnectStep.vue — number from my-status). One copy, so the two
 * can never tell a practice different things. Plain scoped CSS, no SCSS imports, because the public
 * page has none of the wizard's style tokens.
 *
 * Copy rules (swept by cypress/node-tests/whatsapp-sign-in-steps.test.js): no vendor jargon, no
 * promise of a contact channel, no invented timings, and every sentence true on EVERY path —
 * including a practice whose Teamio number IS the number it moved off the app ("on a different
 * number" is what keeps the warning true there).
 */
import { signInNumberParts } from './embeddedSignup';

export default {
  name: 'WhatsAppSignInSteps',

  props: {
    /** The number to enter, as stored (E.164, e.g. '+15615550123'). '' when unknown. */
    phone: { type: String, default: '' },
    /** The WhatsApp name registration will use. '' when unknown. */
    displayName: { type: String, default: '' },
  },

  computed: {
    number() {
      return signInNumberParts(this.phone);
    },
    name() {
      return String(this.displayName || '').trim();
    },
  },
};
</script>

<style scoped>
/* Sizes, weights and list markers are set EXPLICITLY: the app's global styles strip list markers
   (`list-style: none`) and set lists to a light 300 weight, which rendered these steps unnumbered and
   faint — measured on the built page 2026-09-11. Numbered, readable steps are the point here. */
.wa-signin {
  width: 100%;
  max-width: 640px;
  text-align: left;
  color: #262b2a;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.6;
}

.wa-signin__number {
  margin: 0 0 1rem;
  padding: 0.8rem 0.95rem;
  border-radius: 10px;
  background: #e9f6f1;
  border: 2px solid #0f7259;
}

.wa-signin__number--missing {
  background: #fef6e7;
  border-color: #f59e0b;
}

.wa-signin__eyebrow {
  margin: 0;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #0b5c49;
}

.wa-signin__value {
  margin: 0.15rem 0 0;
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  color: #0c0f0e;
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}

.wa-signin__country {
  margin: 0.1rem 0 0;
  font-weight: 600;
  color: #0b5c49;
}

.wa-signin__rule {
  margin: 0.5rem 0 0;
  font-size: 0.95rem;
  font-weight: 400;
}

.wa-signin__number--missing .wa-signin__rule {
  margin: 0;
  color: #b45309;
  font-weight: 600;
}

.wa-signin__heading {
  margin: 0 0 0.35rem;
  font-weight: 700;
  color: #0c0f0e;
}

.wa-signin__steps {
  margin: 0 0 1rem;
  padding-left: 1.5rem;
  list-style: decimal outside;
}

.wa-signin__steps li,
.wa-signin__stop li {
  font-weight: 400;
  color: #262b2a;
}

.wa-signin__steps li::marker {
  font-weight: 800;
  color: #0f7259;
}

.wa-signin__steps li + li {
  margin-top: 0.45rem;
}

/* A phone number broken across two lines is misread; keep it (and the country) whole. */
.wa-signin__nowrap {
  white-space: nowrap;
}

.wa-signin__stop {
  margin: 0 0 1rem;
  padding: 0.7rem 0.9rem;
  border-radius: 10px;
  background: #fdeeee;
  border: 1px solid #f6d5d5;
}

.wa-signin__heading--stop {
  color: #a5292e;
}

.wa-signin__stop ul {
  margin: 0;
  padding-left: 1.3rem;
  list-style: disc outside;
}
</style>
